/**
 * keepInventory.ts — 킵/무기 인벤토리 시스템 (Sprint 1)
 *
 * 슬롯: 시작 1칸 → 골드로 누진 가격 확장, 최대 10칸.
 * 슬롯 확장 비용은 누진 (슬롯 n번째 = SLOT_BASE_COST * SLOT_COST_MULT^(n-1)).
 *
 * 순수 로직만 포함 (브라우저/PIXI 금지).
 */

export interface KeptWeapon {
    level: number;
    /** 보관 시각 (timestamp) — 정렬/표시용 */
    savedAt: number;
}

export interface KeepInventoryState {
    slots: number;           // 현재 슬롯 수 (1~MAX_SLOTS)
    weapons: KeptWeapon[];   // 보관된 무기 목록
}

export const MAX_SLOTS = 10;

/** 슬롯 추가 비용 (n번째 슬롯 구매 시) */
const SLOT_BASE_COST = 50_000;
const SLOT_COST_MULT = 3;

export function emptyKeepInventory(): KeepInventoryState {
    return { slots: 1, weapons: [] };
}

/**
 * 슬롯 확장 비용. 현재 슬롯 수 기준 다음 슬롯 가격.
 * 슬롯이 이미 MAX라면 null.
 */
export function nextSlotCost(state: KeepInventoryState): number | null {
    if (state.slots >= MAX_SLOTS) return null;
    // 2번째 슬롯 = SLOT_BASE_COST, 3번째 = SLOT_BASE_COST*3, ...
    return Math.floor(SLOT_BASE_COST * Math.pow(SLOT_COST_MULT, state.slots - 1));
}

/**
 * 현재 무기를 보관. 슬롯이 꽉 차면 false 반환.
 * @returns 새 state와 성공 여부
 */
export function keepWeapon(
    state: KeepInventoryState,
    weaponLevel: number,
): { state: KeepInventoryState; success: boolean } {
    if (state.weapons.length >= state.slots) {
        return { state, success: false };
    }
    const newWeapon: KeptWeapon = { level: weaponLevel, savedAt: Date.now() };
    return {
        state: { ...state, weapons: [...state.weapons, newWeapon] },
        success: true,
    };
}

/**
 * 보관 무기를 꺼내서 현재 무기로 교체.
 * 현재 무기(currentLevel > 1)는 보관 목록으로 이동.
 * @param index weapons 배열 인덱스
 * @returns 새 state, 꺼낸 무기 레벨, 실패 시 null
 */
export function retrieveWeapon(
    state: KeepInventoryState,
    index: number,
    currentLevel: number,
): { state: KeepInventoryState; retrievedLevel: number } | null {
    if (index < 0 || index >= state.weapons.length) return null;

    const retrieved = state.weapons[index];
    const remaining = state.weapons.filter((_, i) => i !== index);

    // 현재 무기가 1강 이상이면 보관 목록에 추가 (슬롯 꽉 찬 경우 현재 슬롯을 비워서 들어갈 자리 있음)
    const newWeapons = currentLevel > 1
        ? [...remaining, { level: currentLevel, savedAt: Date.now() }]
        : remaining;

    return {
        state: { ...state, weapons: newWeapons },
        retrievedLevel: retrieved.level,
    };
}

/**
 * 보관 무기 판매 (인벤에서 제거).
 */
export function sellKeptWeapon(
    state: KeepInventoryState,
    index: number,
): KeepInventoryState {
    if (index < 0 || index >= state.weapons.length) return state;
    return { ...state, weapons: state.weapons.filter((_, i) => i !== index) };
}

/**
 * 슬롯 확장 (골드 차감은 호출자가 처리).
 * @returns 새 state, 성공 여부
 */
export function expandSlot(
    state: KeepInventoryState,
): { state: KeepInventoryState; success: boolean } {
    if (state.slots >= MAX_SLOTS) return { state, success: false };
    return {
        state: { ...state, slots: state.slots + 1 },
        success: true,
    };
}

/**
 * 현재 보관 가능 여부
 */
export function canKeep(state: KeepInventoryState): boolean {
    return state.weapons.length < state.slots;
}
